import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { feedAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Eye, ExternalLink, Zap, Clock, CheckCircle, 
  RefreshCw, AlertCircle, Trophy, Heart, Mail, X
} from 'lucide-react';
import { toast } from 'sonner';

const Feed = () => {
  const { user, updateUser } = useAuth();
  const [feed, setFeed] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewTimer, setViewTimer] = useState(0);
  const [timerDuration, setTimerDuration] = useState(10);
  const [creditsPerView, setCreditsPerView] = useState(1);
  const [isViewing, setIsViewing] = useState(false);
  const [canClaim, setCanClaim] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [dailyViews, setDailyViews] = useState(user?.daily_views_today || 0);
  const [dailyLimit, setDailyLimit] = useState(50);
  const [liking, setLiking] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const response = await feedAPI.getFeed(20);
      setFeed(response.data.promotions || []);
      setTimerDuration(response.data.settings?.view_timer_seconds || 10);
      setCreditsPerView(response.data.settings?.credits_per_view || 2);
      setDailyLimit(response.data.settings?.daily_view_limit || 50);
      setDailyViews(response.data.settings?.views_today || 0);
    } catch (error) {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    let interval;
    if (isViewing && viewTimer < timerDuration) {
      interval = setInterval(() => {
        setViewTimer(prev => {
          if (prev + 1 >= timerDuration) {
            setCanClaim(true);
            setIsViewing(false);
            return timerDuration;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isViewing, viewTimer, timerDuration]);

  const currentPromo = feed[currentIndex];

  const startViewing = () => {
    if (dailyViews >= dailyLimit) {
      toast.error('Daily view limit reached!');
      return;
    }
    setIsViewing(true);
    setViewTimer(0);
    setCanClaim(false);
  };

  const claimCredits = async () => {
    if (!canClaim || claiming) return;
    
    setClaiming(true);
    try {
      const response = await feedAPI.recordView(currentPromo.id);
      const newCredits = response.data.total_credits;
      const newDailyViews = response.data.daily_views;
      
      updateUser({ credits: newCredits, daily_views_today: newDailyViews });
      setDailyViews(newDailyViews);
      setDailyLimit(response.data.daily_limit);
      
      toast.success(`+${creditsPerView} credits earned!`);
      
      // Move to next promotion
      if (currentIndex < feed.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        // Refresh feed if at end
        await fetchFeed();
        setCurrentIndex(0);
      }
      
      setViewTimer(0);
      setCanClaim(false);
      setIsViewing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim credits');
    } finally {
      setClaiming(false);
    }
  };

  const handleClick = async () => {
    if (!currentPromo) return;
    
    // Open link immediately to avoid popup blocker
    const url = currentPromo.destination_url;
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    
    // Record click in background
    try {
      await feedAPI.recordClick(currentPromo.id);
    } catch (error) {
      console.error('Failed to record click:', error);
    }
    
    // Fallback if popup was blocked
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = url;
    }
  };

  const skipPromotion = () => {
    if (currentIndex < feed.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setViewTimer(0);
      setCanClaim(false);
      setIsViewing(false);
    } else {
      fetchFeed();
      setCurrentIndex(0);
    }
  };

  const handleLike = async () => {
    if (!currentPromo || liking) return;
    
    setLiking(true);
    try {
      const response = await feedAPI.likePromotion(currentPromo.id);
      // Update the current promo in feed
      setFeed(prev => prev.map((p, i) => 
        i === currentIndex ? { ...p, liked: response.data.liked, likes: response.data.likes } : p
      ));
      toast.success(response.data.liked ? 'Liked!' : 'Unliked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to like');
    } finally {
      setLiking(false);
    }
  };

  const handleContactOwner = async () => {
    if (!currentPromo || sendingMessage) return;
    
    if (!contactSubject.trim() || !contactMessage.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSendingMessage(true);
    try {
      await feedAPI.contactOwner(currentPromo.id, contactSubject, contactMessage);
      toast.success('Message sent to promotion owner!');
      setShowContactModal(false);
      setContactSubject('');
      setContactMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="feed-page">
      {/* Stats Bar */}
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-neon-mint" />
            <span className="font-mono font-bold text-white" data-testid="feed-credits">
              {user?.credits || 0}
            </span>
            <span className="text-gray-500">credits</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-neon-amber" />
            <span className="font-mono text-white">{dailyViews}</span>
            <span className="text-gray-500">/ {dailyLimit} today</span>
          </div>
        </div>
        <Button 
          variant="ghost" 
          onClick={fetchFeed}
          className="text-gray-400 hover:text-white"
          data-testid="refresh-feed-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Daily Limit Warning */}
      {dailyViews >= dailyLimit && (
        <div className="bg-neon-amber/10 border border-neon-amber/20 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-neon-amber flex-shrink-0" />
          <div>
            <p className="text-white font-medium">Daily limit reached!</p>
            <p className="text-gray-400 text-sm">Come back tomorrow to earn more credits.</p>
          </div>
        </div>
      )}

      {/* Main Feed Card */}
      {feed.length === 0 ? (
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-neon-mint" />
            </div>
            <h3 className="text-white font-heading font-semibold text-xl mb-2">All caught up!</h3>
            <p className="text-gray-400 mb-4">No more promotions to view right now.</p>
            <Button onClick={fetchFeed} className="bg-neon-mint text-black hover:bg-neon-mint/90">
              <RefreshCw className="w-4 h-4 mr-2" />
              Check Again
            </Button>
          </CardContent>
        </Card>
      ) : currentPromo ? (
        <Card className="bg-void-paper border-white/10 overflow-hidden">
          {/* Progress Bar */}
          {isViewing && (
            <div className="h-1 bg-white/10">
              <div 
                className="h-full bg-neon-mint transition-all duration-1000 ease-linear"
                style={{ width: `${(viewTimer / timerDuration) * 100}%` }}
              />
            </div>
          )}
          
          <CardContent className="p-6 lg:p-8">
            {/* Category Badge */}
            <div className="flex items-center justify-between mb-4">
              <span className="px-3 py-1 bg-white/5 text-gray-400 text-sm rounded-full">
                {currentPromo.category}
              </span>
              <span className="text-gray-500 text-sm">
                {currentIndex + 1} / {feed.length}
              </span>
            </div>
            
            {/* Promo Content */}
            <div className="mb-6">
              <h2 className="font-heading font-bold text-2xl text-white mb-3">
                {currentPromo.title}
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                {currentPromo.description}
              </p>
            </div>
            
            {/* Promo Image (if available) */}
            {currentPromo.image_url && (
              <div className="mb-6 rounded-xl overflow-hidden">
                <img 
                  src={currentPromo.image_url} 
                  alt={currentPromo.title}
                  className="w-full h-48 object-cover"
                />
              </div>
            )}
            
            {/* Timer Display */}
            {isViewing && !canClaim && (
              <div className="mb-6 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full">
                  <Clock className="w-5 h-5 text-neon-amber" />
                  <span className="font-mono text-2xl text-white">
                    {timerDuration - viewTimer}s
                  </span>
                </div>
                <p className="text-gray-500 text-sm mt-2">Keep viewing to earn credits</p>
              </div>
            )}
            
            {/* Success State */}
            {canClaim && (
              <div className="mb-6 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-neon-mint/10 rounded-full animate-pulse-glow">
                  <CheckCircle className="w-5 h-5 text-neon-mint" />
                  <span className="font-bold text-neon-mint">+{creditsPerView} credit ready!</span>
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {!isViewing && !canClaim && (
                <Button 
                  onClick={startViewing}
                  disabled={dailyViews >= dailyLimit}
                  className="flex-1 h-12 bg-neon-mint text-black hover:bg-neon-mint/90 font-bold shadow-neon btn-press"
                  data-testid="start-viewing-btn"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Start Viewing (+{creditsPerView} credit)
                </Button>
              )}
              
              {isViewing && !canClaim && (
                <Button 
                  disabled
                  className="flex-1 h-12 bg-white/10 text-white cursor-not-allowed"
                >
                  <Clock className="w-5 h-5 mr-2 animate-pulse" />
                  Viewing... {timerDuration - viewTimer}s left
                </Button>
              )}
              
              {canClaim && (
                <Button 
                  onClick={claimCredits}
                  disabled={claiming}
                  className="flex-1 h-12 bg-neon-mint text-black hover:bg-neon-mint/90 font-bold shadow-neon animate-pulse-neon btn-press"
                  data-testid="claim-credits-btn"
                >
                  {claiming ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Claim {creditsPerView} Credit
                    </>
                  )}
                </Button>
              )}
              
              <Button 
                onClick={handleClick}
                variant="outline"
                className="h-12 border-white/20 text-white hover:bg-white/5"
                data-testid="visit-link-btn"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Visit Link
              </Button>
              
              <Button 
                onClick={skipPromotion}
                variant="ghost"
                className="h-12 text-gray-400 hover:text-white"
                data-testid="skip-btn"
              >
                Skip
              </Button>
            </div>
            
            {/* Like and Contact Row */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10">
              <Button
                onClick={handleLike}
                disabled={liking}
                variant="ghost"
                className={`flex items-center gap-2 ${currentPromo.liked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}
                data-testid="like-btn"
              >
                <Heart className={`w-5 h-5 ${currentPromo.liked ? 'fill-current' : ''}`} />
                <span>{currentPromo.likes || 0}</span>
              </Button>
              
              {(user?.plan === 'pro' || user?.plan === 'power') && (
                <Button
                  onClick={() => setShowContactModal(true)}
                  variant="ghost"
                  className="flex items-center gap-2 text-gray-400 hover:text-neon-violet"
                  data-testid="contact-owner-btn"
                >
                  <Mail className="w-5 h-5" />
                  <span>Contact Owner</span>
                </Button>
              )}
              
              {user?.plan === 'free' && (
                <span className="text-gray-500 text-sm flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  Upgrade to contact owners
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Tips */}
      <div className="bg-void-subtle border border-white/10 rounded-xl p-4">
        <h4 className="text-white font-medium mb-2">Tips to earn more:</h4>
        <ul className="text-gray-400 text-sm space-y-1">
          <li>• View promotions completely to earn credits</li>
          <li>• Claim your daily login bonus</li>
          <li>• Upgrade to Pro for higher daily limits</li>
        </ul>
      </div>
      
      {/* Contact Owner Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-void-paper border border-white/10 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-heading font-bold text-xl">Contact Owner</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowContactModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <p className="text-gray-400 text-sm mb-4">
              Ask questions about: <span className="text-neon-mint">{currentPromo?.title}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Subject</label>
                <input
                  type="text"
                  value={contactSubject}
                  onChange={(e) => setContactSubject(e.target.value)}
                  placeholder="e.g., Question about your product"
                  className="w-full px-4 py-2 bg-void border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-neon-violet focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Message</label>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Write your message here..."
                  rows={4}
                  className="w-full px-4 py-2 bg-void border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-neon-violet focus:outline-none resize-none"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowContactModal(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleContactOwner}
                  disabled={sendingMessage}
                  className="flex-1 bg-neon-violet text-white hover:bg-neon-violet/90"
                >
                  {sendingMessage ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feed;
